import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  useTheme,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import { 
  ViewColumn as ViewColumnIcon,
  FilterList as FilterListIcon,
  Download
} from '@mui/icons-material';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { FilterSelector, FilterConfig } from '../components/FilterSelector';
import ExportSelector from '../components/ExportSelector';
import { exportToCsv, exportToExcel, generateFilename } from '../utils/exportUtils';
import { alarmsService } from '../services/api';
import { AlarmData } from '../types';

interface AlarmEvent {
  id: string;
  timestamp: Date;
  category: string;
  name: string;
  head: string;
}


const ChargerAlarmsEvents: React.FC = () => {
  const theme = useTheme();
  const { orgId, unitId } = useParams<{ orgId: string; unitId: string }>();
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [filterSelectorOpen, setFilterSelectorOpen] = useState(false);
  const [exportSelectorOpen, setExportSelectorOpen] = useState(false);
  const [alarmData, setAlarmData] = useState<AlarmEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const columnButtonRef = useRef<HTMLButtonElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);

  // Define available columns
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'timestamp', label: 'Timestamp', visible: true },
    { key: 'category', label: 'Category', visible: true },
    { key: 'name', label: 'Name', visible: true },
    { key: 'head', label: 'Head', visible: true },
  ]);

  // Define filterable columns for the filter selector
  const filterableColumns = [
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'category', label: 'Category' },
    { key: 'name', label: 'Name' },
    { key: 'head', label: 'Head' },
  ];

  // Filter state
  const [filters, setFilters] = useState<FilterConfig[]>([]);

  // Fetch alarm data from API
  useEffect(() => {
    const fetchAlarmData = async () => {
      if (!orgId || !unitId) {
        setError('Missing organization ID or unit ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching alarm data for orgId:', orgId, 'unitId:', unitId);
        const response = await alarmsService.getAlarms(orgId, unitId);
        
        if (response.success && response.data) {
          // Transform API data to component format
          const transformedData: AlarmEvent[] = response.data.data.map((alarm: AlarmData) => ({
            id: alarm.alarm_id,
            timestamp: new Date(), // API doesn't provide timestamp, using current time
            category: alarm.category,
            name: alarm.name,
            head: alarm.head,
          }));
          
          console.log('Alarm data loaded:', transformedData);
          setAlarmData(transformedData);
        } else {
          console.error('No alarm data found');
          setAlarmData([]);
        }
      } catch (err) {
        console.error('Failed to load alarm data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load alarm data');
        setAlarmData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAlarmData();
  }, [orgId, unitId]);

  // Apply filters to data
  const applyFilters = (data: AlarmEvent[]) => {
    return data.filter(item => {
      return filters.every(filter => {
        if (filter.type === 'date' && filter.column === 'timestamp') {
          const itemDate = new Date(item.timestamp);
          const startDate = new Date(filter.startDate!);
          const endDate = new Date(filter.endDate!);
          return itemDate >= startDate && itemDate <= endDate;
        } else if (filter.type === 'text') {
          const itemValue = String(item[filter.column as keyof AlarmEvent]).toLowerCase();
          return itemValue.includes(filter.value.toLowerCase());
        }
        return true;
      });
    });
  };

  const filteredData = applyFilters(alarmData);

  const handleColumnsChange = (newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
  };

  const handleFiltersChange = (newFilters: FilterConfig[]) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCsv = () => {
    const filename = generateFilename('charger_alarms', 'csv');
    exportToCsv(filteredData, columns, filename);
  };

  const handleExportExcel = () => {
    const filename = generateFilename('charger_alarms', 'xlsx');
    exportToExcel(filteredData, columns, filename);
  };

  const getVisibleColumns = () => {
    return columns.filter(column => column.visible);
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Alarms
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 3, gap: 1, position: 'relative' }}>
        <Button
          ref={columnButtonRef}
          variant="text"
          startIcon={<ViewColumnIcon />}
          onClick={() => setColumnSelectorOpen(!columnSelectorOpen)}
          sx={{
            color: '#337A5C',
          }}
        >
          COLUMNS
        </Button>

        <Button
          ref={filterButtonRef}
          variant="text"
          startIcon={<FilterListIcon />}
          onClick={() => setFilterSelectorOpen(!filterSelectorOpen)}
          sx={{
            color: '#337A5C',
          }}
        >
          FILTERS
        </Button>

        <Button
          ref={exportButtonRef}
          variant="text"
          startIcon={<Download />}
          onClick={() => setExportSelectorOpen(!exportSelectorOpen)}
          sx={{
            color: '#337A5C',
          }}
        >
          EXPORT
        </Button>

      </Box>

      {/* Column Selector */}
      <ColumnSelector
        columns={columns}
        onColumnsChange={handleColumnsChange}
        open={columnSelectorOpen}
        onClose={() => setColumnSelectorOpen(false)}
        anchorEl={columnButtonRef.current}
      />

      {/* Filter Selector */}
      <FilterSelector
        columns={filterableColumns}
        onFiltersChange={handleFiltersChange}
        open={filterSelectorOpen}
        onClose={() => setFilterSelectorOpen(false)}
        anchorEl={filterButtonRef.current}
      />

      {/* Export Selector */}
      <ExportSelector
        open={exportSelectorOpen}
        onClose={() => setExportSelectorOpen(false)}
        anchorEl={exportButtonRef.current}
        onExportCsv={handleExportCsv}
        onExportExcel={handleExportExcel}
      />

      {/* RFID Access Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {getVisibleColumns().map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No alarm data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                  {getVisibleColumns().map((column) => (
                    <TableCell key={column.key}>
                      <Typography variant="body2" sx={{ fontWeight: 400, color: theme.palette.text.primary }}>
                        {column.key === 'timestamp' && row.timestamp.toLocaleString()}
                        {column.key === 'category' && row.category}
                        {column.key === 'name' && row.name}
                        {column.key === 'head' && row.head}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />
    </Box>
  );
};

export default ChargerAlarmsEvents;
